import { useEffect, useState } from 'react';
import { Webhook, Plus, Edit3, Trash2, Send, Check, AlertCircle, Copy, MessageSquare, Zap, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('masterToken')}` });

const EVENT_GROUPS = [
  { label: 'Bookings', events: ['booking.created', 'booking.accepted', 'booking.completed'] },
  { label: 'Communication', events: ['message.received'] },
  { label: 'Reviews & Trust', events: ['review.posted', 'complaint.reported', 'vendor.id_approved'] },
  { label: 'Ops Alerts', events: ['auth.signup_failed'] },
];

export default function MasterIntegrations() {
  const [webhooks, setWebhooks] = useState([]);
  const [allowedEvents, setAllowedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | webhook_id
  const [form, setForm] = useState({ url: '', label: '', events: [], format: 'generic' });

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/master/webhooks`, { headers: authHeaders() });
      if (res.ok) {
        const d = await res.json();
        setWebhooks(d.webhooks || []);
        setAllowedEvents(d.allowed_events || []);
      }
    } catch {
      toast.error('Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // ─── Mailchimp config ──────────────────────────────────────────────
  const [mcStatus, setMcStatus] = useState(null);
  const [mcForm, setMcForm] = useState({ api_key: '', audience_id: '', server_prefix: '' });
  const [mcSaving, setMcSaving] = useState(false);

  // ─── Meta Pixel + CAPI config ──────────────────────────────────────
  const [metaStatus, setMetaStatus] = useState(null);
  const [metaForm, setMetaForm] = useState({ pixel_id: '', capi_access_token: '', test_event_code: '', catalog_id: '', ad_account_id: '' });
  const [metaSaving, setMetaSaving] = useState(false);
  const [metaTesting, setMetaTesting] = useState(false);

  // ─── Custom Audiences ──────────────────────────────────────────────
  const [audiences, setAudiences] = useState([]);
  const [audienceSpecs, setAudienceSpecs] = useState([]);
  const [seeding, setSeeding] = useState(false);

  const loadAudiences = async () => {
    try {
      const res = await fetch(`${API}/master/integrations/meta/audiences`, { headers: authHeaders() });
      if (res.ok) {
        const d = await res.json();
        setAudiences(d.audiences || []);
        setAudienceSpecs(d.available_specs || []);
      }
    } catch {}
  };
  useEffect(() => { loadAudiences(); }, []);

  const seedAudiences = async () => {
    setSeeding(true);
    try {
      const res = await fetch(`${API}/master/integrations/meta/audiences/seed`, { method: 'POST', headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        if (data.created > 0) toast.success(`${data.created} audience(s) created!`);
        else toast('All starter audiences already exist.', { icon: '✅' });
        if (data.errors?.length) toast.error(`${data.errors.length} failed — check console`);
        if (data.errors?.length) console.warn('Audience seed errors:', data.errors);
        loadAudiences();
      } else {
        toast.error(data.detail || 'Could not seed audiences');
      }
    } finally { setSeeding(false); }
  };

  const deleteAudience = async (slug) => {
    if (!window.confirm('Delete this audience from Meta? You can re-create it via "Seed Starter Audiences".')) return;
    const res = await fetch(`${API}/master/integrations/meta/audiences/${slug}`, { method: 'DELETE', headers: authHeaders() });
    if (res.ok) { toast.success('Audience deleted'); loadAudiences(); }
    else toast.error('Delete failed');
  };

  const loadMetaStatus = async () => {
    try {
      const res = await fetch(`${API}/master/integrations/meta/status`, { headers: authHeaders() });
      if (res.ok) setMetaStatus(await res.json());
    } catch {}
  };
  useEffect(() => { loadMetaStatus(); }, []);

  const saveMeta = async () => {
    if (!metaForm.pixel_id || !metaForm.capi_access_token) {
      toast.error('Pixel ID and CAPI Token are required');
      return;
    }
    setMetaSaving(true);
    try {
      const res = await fetch(`${API}/master/integrations/meta`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(metaForm),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success('Meta connected!');
        if (data.audiences_seeded > 0) {
          toast.success(`${data.audiences_seeded} starter audience(s) auto-created!`);
        }
        if (data.audience_errors?.length) {
          console.warn('Audience seed errors:', data.audience_errors);
          toast.error(`${data.audience_errors.length} audience(s) failed — check ad-account permissions`);
        }
        setMetaForm({ pixel_id: '', capi_access_token: '', test_event_code: '', catalog_id: '', ad_account_id: '' });
        loadMetaStatus();
        loadAudiences();
      } else {
        toast.error(data.detail || 'Could not connect Meta');
      }
    } finally { setMetaSaving(false); }
  };

  const disconnectMeta = async () => {
    if (!confirm('Disconnect Meta? Pixel will stop loading on the site and CAPI events will stop sending.')) return;
    await fetch(`${API}/master/integrations/meta`, { method: 'DELETE', headers: authHeaders() });
    toast.success('Meta disconnected');
    loadMetaStatus();
  };

  const fireMetaTest = async () => {
    setMetaTesting(true);
    try {
      const res = await fetch(`${API}/master/integrations/meta/test-event`, { method: 'POST', headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.mirrored) {
        toast.success('Test event sent — check Events Manager → Test Events');
      } else {
        toast.error(data.error || 'Test event did not reach Meta');
      }
    } finally { setMetaTesting(false); }
  };

  const loadMcStatus = async () => {
    try {
      const res = await fetch(`${API}/master/integrations/mailchimp/status`, { headers: authHeaders() });
      if (res.ok) setMcStatus(await res.json());
    } catch {}
  };
  useEffect(() => { loadMcStatus(); }, []);

  const saveMailchimp = async () => {
    if (!mcForm.api_key || !mcForm.audience_id || !mcForm.server_prefix) {
      toast.error('All three fields required');
      return;
    }
    setMcSaving(true);
    try {
      const res = await fetch(`${API}/master/integrations/mailchimp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(mcForm),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success('Mailchimp connected!');
        setMcForm({ api_key: '', audience_id: '', server_prefix: '' });
        loadMcStatus();
      } else {
        toast.error(data.detail || 'Could not connect Mailchimp');
      }
    } finally { setMcSaving(false); }
  };

  const disconnectMailchimp = async () => {
    if (!confirm('Disconnect Mailchimp? Subscribers will keep going into our internal list as a fallback.')) return;
    await fetch(`${API}/master/integrations/mailchimp`, { method: 'DELETE', headers: authHeaders() });
    toast.success('Mailchimp disconnected');
    loadMcStatus();
  };

  const startEdit = (wh) => {
    if (wh === 'new') {
      setForm({ url: '', label: '', events: ['booking.created'], format: 'generic' });
      setEditing('new');
    } else {
      setForm({ url: wh.url, label: wh.label || '', events: wh.events || [], format: wh.format || 'generic' });
      setEditing(wh.webhook_id);
    }
  };

  const save = async () => {
    if (!form.url || !form.url.startsWith('http')) {
      toast.error('Paste a full webhook URL (https://…)');
      return;
    }
    if (!form.events.length) {
      toast.error('Pick at least one event');
      return;
    }
    try {
      const url = editing === 'new' ? `${API}/master/webhooks` : `${API}/master/webhooks/${editing}`;
      const method = editing === 'new' ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success(editing === 'new' ? 'Webhook added' : 'Webhook updated');
        setEditing(null);
        load();
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Failed');
      }
    } catch {
      toast.error('Network error');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this webhook?')) return;
    await fetch(`${API}/master/webhooks/${id}`, { method: 'DELETE', headers: authHeaders() });
    load();
  };

  const sendTest = async (id) => {
    toast('Firing test delivery…');
    const res = await fetch(`${API}/master/webhooks/${id}/test`, { method: 'POST', headers: authHeaders() });
    if (res.ok) {
      setTimeout(load, 1500);
      toast.success('Test fired — check last_status');
    } else {
      toast.error('Test failed');
    }
  };

  const toggleEnabled = async (wh) => {
    await fetch(`${API}/master/webhooks/${wh.webhook_id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ enabled: !wh.enabled }),
    });
    load();
  };

  return (
    <div className="space-y-8" data-testid="master-integrations-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white font-syne">Integrations</h1>
        <p className="text-[#9CA3AF] text-sm mt-1">
          Pipe marketplace events to Slack, Zapier, Make, n8n, Discord, or any generic webhook receiver.
        </p>
      </div>

      {/* ─────────────────── Meta Pixel Setup Guide ─────────────────── */}
      {!metaStatus?.configured && (
        <div className="rounded-2xl border border-blue-400/30 bg-gradient-to-br from-blue-500/10 via-purple-500/5 to-fuchsia-500/10 p-6" data-testid="meta-setup-guide">
          <div className="flex items-start gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
              <Zap size={18} className="text-blue-300" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white font-syne">Get your Meta Pixel running in 5 minutes</h2>
              <p className="text-[#9CA3AF] text-xs mt-1">
                A guided walkthrough — copy your Pixel ID & CAPI token from Meta, paste below, and we'll do the rest
                (pageviews, signups, follows, bookings, purchases) automatically.
              </p>
            </div>
          </div>
          <ol className="space-y-3 text-sm">
            {[
              {
                title: 'Open Meta Business Manager',
                body: (
                  <>
                    Sign in at <a href="https://business.facebook.com/" target="_blank" rel="noreferrer" className="text-blue-300 hover:underline">business.facebook.com</a> with the personal account that owns your ad account.
                  </>
                ),
              },
              {
                title: 'Go to Events Manager → Connect Data Sources → Web',
                body: (
                  <>
                    Open <a href="https://business.facebook.com/events_manager2" target="_blank" rel="noreferrer" className="text-blue-300 hover:underline">Events Manager</a>, click <b>Connect Data Sources</b>, choose <b>Web</b>, then <b>Meta Pixel</b>, and name it <span className="font-mono text-fuchsia-300">EntrepreNatives Pixel</span>.
                  </>
                ),
              },
              {
                title: 'Copy your Pixel ID',
                body: (
                  <>
                    Once created, Events Manager shows a 15- or 16-digit number — that's your Pixel ID. Paste it into the form below.
                  </>
                ),
              },
              {
                title: 'Create a CAPI Access Token',
                body: (
                  <>
                    In the same Events Manager page, click <b>Settings → Conversions API → Generate Access Token</b>. Copy the long <span className="font-mono text-fuchsia-300">EAA…</span> token and paste it below. (Optional but recommended — server-side mirror keeps tracking working even when ad-blockers hit the browser pixel.)
                  </>
                ),
              },
              {
                title: 'Find your Ad Account ID (optional but recommended)',
                body: (
                  <>
                    In <a href="https://business.facebook.com/settings/ad-accounts" target="_blank" rel="noreferrer" className="text-blue-300 hover:underline">Business Settings → Ad Accounts</a>, copy the ID (looks like <span className="font-mono text-fuchsia-300">act_1234567890</span>). Pasting it below lets us <b>automatically create 6 starter Custom Audiences</b> — retargeting, lookalikes, abandoned-signup pools — the moment you click Connect.
                  </>
                ),
              },
              {
                title: 'Paste everything below and click Connect Meta',
                body: (
                  <>
                    That's it — we'll automatically fire <b>PageView</b>, <b>Lead</b> (when someone starts signing up), <b>CompleteRegistration</b>, <b>ViewContent</b> (vendor profile views), <b>AddToCart</b> (follow signal), and <b>Purchase</b> (every paid booking) — both via the browser pixel AND via CAPI server-side mirror with full deduplication.
                  </>
                ),
              },
            ].map((s, i) => (
              <li key={i} className="flex items-start gap-3" data-testid={`meta-step-${i + 1}`}>
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-fuchsia-400 text-[#0E0E23] text-xs font-bold flex items-center justify-center">
                  {i + 1}
                </span>
                <div className="flex-1">
                  <p className="text-white font-semibold">{s.title}</p>
                  <p className="text-[#9CA3AF] text-xs mt-0.5">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <a
              href="https://business.facebook.com/events_manager2"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm bg-blue-500 hover:bg-blue-400 text-white font-semibold transition-colors"
              data-testid="meta-open-events-manager"
            >
              Open Events Manager <ArrowUpRight size={14} />
            </a>
            <a
              href="https://chromewebstore.google.com/detail/meta-pixel-helper/fdgfkebogiimcoedlicjlajpkdmockpc"
              target="_blank"
              rel="noreferrer"
              className="text-xs text-blue-300 hover:underline"
            >
              Install Meta Pixel Helper extension (verifies it's firing) →
            </a>
          </div>
        </div>
      )}

      {/* ─────────────────── Meta Pixel + CAPI ─────────────────── */}
      <div className="rounded-2xl bg-white/5 border border-white/10 p-6" data-testid="meta-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-white font-syne flex items-center gap-2">
              <Zap size={16} className="text-blue-400" /> Meta Pixel · CAPI · Catalog
            </h2>
            <p className="text-[#9CA3AF] text-xs mt-1">Site-wide pixel (consent-gated) + server-side Conversions API mirror + auto-generated product catalog feed.</p>
          </div>
          {metaStatus?.configured ? (
            <span className="px-3 py-1 rounded-full bg-green-500/15 text-green-400 text-xs font-semibold border border-green-500/20 flex items-center gap-1.5"><Check size={12} /> Connected</span>
          ) : (
            <span className="px-3 py-1 rounded-full bg-zinc-500/15 text-zinc-300 text-xs font-semibold border border-zinc-500/20">Not Connected</span>
          )}
        </div>

        {metaStatus?.configured ? (
          <div className="text-sm space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="text-[#9CA3AF]">Pixel ID: <span className="text-white font-mono text-xs">{metaStatus.pixel_id}</span></div>
              <div className="text-[#9CA3AF]">CAPI Token: <span className="text-white">••••••• Set</span></div>
              {metaStatus.test_event_code && (
                <div className="text-[#9CA3AF]">Test Event Code: <span className="text-white font-mono text-xs">{metaStatus.test_event_code}</span></div>
              )}
              <div className="text-[#9CA3AF]">Total events mirrored: <span className="text-white">{metaStatus.events_sent_total ?? 0}</span></div>
            </div>

            {/* Events firing live — confirms every signal Claude's playbook called out */}
            <div className="mt-3 rounded-lg bg-green-500/5 border border-green-500/20 px-3 py-2.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-green-300 mb-1.5">Auto-firing events</p>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { name: 'PageView', where: 'Every route change' },
                  { name: 'Lead', where: 'Signup form started · Follow vendor' },
                  { name: 'CompleteRegistration', where: 'Successful signup' },
                  { name: 'ViewContent', where: 'Vendor profile views' },
                  { name: 'AddToCart', where: 'Post engagement signal' },
                  { name: 'Purchase', where: 'Paid booking' },
                ].map((e) => (
                  <span key={e.name} title={e.where} className="px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/30 text-green-200 text-[10px] font-semibold">
                    {e.name}
                  </span>
                ))}
              </div>
              <p className="text-[10px] text-green-200/70 mt-1.5">All events deduplicated across browser pixel + server-side CAPI.</p>
            </div>

            <div className="rounded-lg bg-blue-500/5 border border-blue-500/20 px-3 py-2 text-xs text-blue-200">
              <p className="font-semibold mb-1">Catalog feeds (paste into Commerce Manager → Data Sources → Scheduled Feed):</p>
              <div className="flex items-center gap-2 mt-1">
                <p className="font-mono break-all flex-1">{`${process.env.REACT_APP_BACKEND_URL}/api/meta/catalog.xml`}</p>
                <button
                  onClick={() => { navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/meta/catalog.xml`); toast.success('XML feed URL copied'); }}
                  className="px-2 py-1 rounded bg-blue-500/20 hover:bg-blue-500/30 text-blue-100 text-[10px] font-semibold"
                  data-testid="meta-copy-xml"
                >
                  <Copy size={11} className="inline mr-1" />Copy
                </button>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <p className="font-mono break-all flex-1">{`${process.env.REACT_APP_BACKEND_URL}/api/meta/catalog.csv`}</p>
                <button
                  onClick={() => { navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/meta/catalog.csv`); toast.success('CSV feed URL copied'); }}
                  className="px-2 py-1 rounded bg-blue-500/20 hover:bg-blue-500/30 text-blue-100 text-[10px] font-semibold"
                  data-testid="meta-copy-csv"
                >
                  <Copy size={11} className="inline mr-1" />Copy
                </button>
              </div>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button onClick={fireMetaTest} disabled={metaTesting} className="px-3 py-1.5 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 text-xs font-semibold disabled:opacity-50" data-testid="meta-test-event">
                {metaTesting ? 'Sending…' : 'Send Test Event'}
              </button>
              <a href="https://business.facebook.com/events_manager2" target="_blank" rel="noreferrer" className="text-xs text-blue-300 hover:underline inline-flex items-center gap-1">
                Open Events Manager <ArrowUpRight size={11} />
              </a>
              <button onClick={disconnectMeta} className="text-xs text-red-400 hover:text-red-300 ml-auto" data-testid="meta-disconnect">Disconnect</button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1 block">Pixel ID</label>
              <input type="text" value={metaForm.pixel_id} onChange={(e) => setMetaForm({ ...metaForm, pixel_id: e.target.value })} placeholder="123456789012345" className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#A78BFA]/50" data-testid="meta-pixel-id" />
            </div>
            <div>
              <label className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1 block">CAPI Access Token</label>
              <input type="password" value={metaForm.capi_access_token} onChange={(e) => setMetaForm({ ...metaForm, capi_access_token: e.target.value })} placeholder="EAAxxxxxxxxxx" className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#A78BFA]/50" data-testid="meta-capi-token" />
            </div>
            <div>
              <label className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1 block">Test Event Code (optional)</label>
              <input type="text" value={metaForm.test_event_code} onChange={(e) => setMetaForm({ ...metaForm, test_event_code: e.target.value })} placeholder="TEST12345" className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#A78BFA]/50" data-testid="meta-test-event-code" />
            </div>
            <div>
              <label className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1 block">Catalog ID (optional)</label>
              <input type="text" value={metaForm.catalog_id} onChange={(e) => setMetaForm({ ...metaForm, catalog_id: e.target.value })} placeholder="123456789012345" className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#A78BFA]/50" data-testid="meta-catalog-id" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] text-fuchsia-300 uppercase tracking-wider mb-1 block flex items-center gap-1.5">
                Ad Account ID
                <span className="px-1.5 py-0.5 rounded bg-fuchsia-500/20 text-fuchsia-200 text-[9px] font-bold normal-case tracking-normal">enables auto Custom Audiences</span>
              </label>
              <input
                type="text"
                value={metaForm.ad_account_id}
                onChange={(e) => setMetaForm({ ...metaForm, ad_account_id: e.target.value })}
                placeholder="act_1234567890"
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-fuchsia-400/30 text-white text-sm focus:outline-none focus:border-fuchsia-400/70 focus:bg-fuchsia-500/5"
                data-testid="meta-ad-account-id"
              />
              <p className="text-[11px] text-[#9CA3AF] mt-1">
                Find this in <a href="https://business.facebook.com/settings/ad-accounts" target="_blank" rel="noreferrer" className="text-fuchsia-300 hover:underline">Business Settings → Ad Accounts</a>. Paste with or without the <span className="font-mono">act_</span> prefix. If provided, we'll automatically create 6 starter Custom Audiences once you click Connect.
              </p>
            </div>
            <div className="md:col-span-2 flex items-center justify-between">
              <p className="text-[11px] text-[#64748B]">
                Get keys from <a href="https://business.facebook.com/events_manager2" target="_blank" rel="noreferrer" className="text-[#A78BFA] hover:underline">Events Manager → Pixel → Settings</a> → CAPI tab. Pixel only loads on the site after a user accepts the cookie banner.
              </p>
              <button onClick={saveMeta} disabled={metaSaving} className="px-4 py-2 rounded-xl text-sm bg-yellow-400 hover:bg-yellow-300 text-black font-bold disabled:opacity-60" data-testid="meta-connect">{metaSaving ? 'Connecting…' : 'Connect Meta'}</button>
            </div>
          </div>
        )}
      </div>

      {/* ─────────────────── Custom Audiences ─────────────────── */}
      {metaStatus?.configured && (
        <div className="rounded-2xl bg-white/5 border border-white/10 p-6" data-testid="audiences-card">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold text-white font-syne flex items-center gap-2">
                <Zap size={16} className="text-fuchsia-400" /> Custom Audiences
              </h2>
              <p className="text-[#9CA3AF] text-xs mt-1">
                Auto-seeded retargeting + Lookalike audiences. They start empty and grow as the pixel collects data.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {metaStatus.ad_account_id && (
                <span className="text-[10px] text-[#9CA3AF]">Ad Account: <span className="font-mono text-white">{metaStatus.ad_account_id}</span></span>
              )}
              <button
                onClick={seedAudiences}
                disabled={seeding || !metaStatus.ad_account_id}
                className="px-3 py-1.5 rounded-lg bg-fuchsia-500/20 hover:bg-fuchsia-500/30 disabled:opacity-40 text-fuchsia-200 text-xs font-semibold"
                data-testid="audiences-seed"
                title={!metaStatus.ad_account_id ? 'Add an Ad Account ID first (disconnect → re-connect with one)' : 'Re-run the starter audience seed (idempotent)'}
              >
                {seeding ? 'Seeding…' : audiences.length === 0 ? 'Seed Starter Audiences' : 'Re-seed Missing'}
              </button>
            </div>
          </div>

          {!metaStatus.ad_account_id && (
            <div className="rounded-lg bg-amber-500/10 border border-amber-400/30 px-3 py-2.5 text-xs text-amber-100">
              <b>Add your Ad Account ID</b> to enable auto-seeding. Disconnect Meta above, then re-connect with the Ad Account ID field filled in
              (e.g. <span className="font-mono">act_1234567890</span>) — we'll create the 6 starter audiences automatically.
            </div>
          )}

          {audiences.length === 0 && metaStatus.ad_account_id && (
            <div className="rounded-lg bg-fuchsia-500/5 border border-fuchsia-400/20 px-3 py-2.5 text-xs text-fuchsia-100">
              No audiences yet. Click <b>Seed Starter Audiences</b> to create the 6 starter audiences listed below.
            </div>
          )}

          {audiences.length > 0 && (
            <div className="space-y-2">
              {audiences.map((a) => (
                <div key={a.slug} className="rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2.5 flex items-start justify-between gap-3" data-testid={`audience-${a.slug}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-white text-sm">{a.name}</p>
                      {a.rule_event && (
                        <span className="px-1.5 py-0.5 rounded bg-green-500/15 text-green-300 text-[10px] font-bold border border-green-500/20">{a.rule_event}</span>
                      )}
                      {a.exclude_event && (
                        <span className="px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 text-[10px] font-bold border border-red-500/20">EXCLUDE {a.exclude_event}</span>
                      )}
                      <span className="text-[10px] text-[#9CA3AF]">{a.retention_days}d retention</span>
                    </div>
                    <p className="text-[11px] text-[#9CA3AF] mt-0.5">{a.description}</p>
                    {a.audience_id && (
                      <a
                        href={`https://www.facebook.com/adsmanager/audiences?act=${(metaStatus.ad_account_id || '').replace('act_', '')}&selected_audience_ids=${a.audience_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[10px] text-fuchsia-300 hover:underline mt-1 inline-block"
                      >
                        View in Ads Manager → {a.audience_id} <ArrowUpRight size={9} className="inline" />
                      </a>
                    )}
                  </div>
                  <button
                    onClick={() => deleteAudience(a.slug)}
                    className="flex-shrink-0 p-1.5 rounded-lg text-red-400 hover:bg-red-500/10"
                    title="Delete this audience"
                    data-testid={`audience-delete-${a.slug}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Preview of what will be created */}
          {audiences.length === 0 && audienceSpecs.length > 0 && (
            <div className="mt-3 space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-[#64748B] font-bold mb-1">Starter set we'll create:</p>
              {audienceSpecs.map((s) => (
                <div key={s.slug} className="rounded-lg bg-white/[0.02] border border-white/5 px-3 py-1.5">
                  <p className="text-white text-xs font-semibold">{s.name}</p>
                  <p className="text-[#9CA3AF] text-[11px]">{s.description}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─────────────────── Mailchimp ─────────────────── */}
      <div className="rounded-2xl bg-white/5 border border-white/10 p-6" data-testid="mailchimp-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-white font-syne flex items-center gap-2">
              <Send size={16} className="text-yellow-400" /> Mailchimp Newsletter
            </h2>
            <p className="text-[#9CA3AF] text-xs mt-1">Sync footer subscribers + signup-checkbox users into your Mailchimp audience with auto-tagging.</p>
          </div>
          {mcStatus?.configured ? (
            <span className="px-3 py-1 rounded-full bg-green-500/15 text-green-400 text-xs font-semibold border border-green-500/20 flex items-center gap-1.5"><Check size={12} /> Connected</span>
          ) : (
            <span className="px-3 py-1 rounded-full bg-zinc-500/15 text-zinc-300 text-xs font-semibold border border-zinc-500/20">Not Connected</span>
          )}
        </div>

        {mcStatus?.configured ? (
          <div className="text-sm text-[#9CA3AF] space-y-1">
            <div>Audience ID: <span className="text-white font-mono text-xs">{mcStatus.audience_id}</span></div>
            <div>Server prefix: <span className="text-white font-mono text-xs">{mcStatus.server_prefix}</span></div>
            <div>Last updated: <span className="text-white">{mcStatus.updated_at?.split('T')[0]}</span></div>
            <button onClick={disconnectMailchimp} className="mt-3 text-xs text-red-400 hover:text-red-300" data-testid="mailchimp-disconnect">Disconnect</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1 block">API Key</label>
              <input type="password" value={mcForm.api_key} onChange={(e) => setMcForm({ ...mcForm, api_key: e.target.value })} placeholder="••••••••-us21" className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#A78BFA]/50" data-testid="mc-api-key" />
            </div>
            <div>
              <label className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1 block">Audience (List) ID</label>
              <input type="text" value={mcForm.audience_id} onChange={(e) => setMcForm({ ...mcForm, audience_id: e.target.value })} placeholder="abc123def4" className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#A78BFA]/50" data-testid="mc-audience-id" />
            </div>
            <div>
              <label className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1 block">Server Prefix</label>
              <input type="text" value={mcForm.server_prefix} onChange={(e) => setMcForm({ ...mcForm, server_prefix: e.target.value })} placeholder="us21" className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#A78BFA]/50" data-testid="mc-server-prefix" />
            </div>
            <div className="md:col-span-3 flex items-center justify-between">
              <p className="text-[11px] text-[#64748B]">
                Get keys from <a href="https://admin.mailchimp.com/account/api/" target="_blank" rel="noreferrer" className="text-[#A78BFA] hover:underline">Mailchimp → Account → API Keys</a> · Audience ID: <a href="https://mailchimp.com/help/find-audience-id/" target="_blank" rel="noreferrer" className="text-[#A78BFA] hover:underline">find here</a>
              </p>
              <button onClick={saveMailchimp} disabled={mcSaving} className="px-4 py-2 rounded-xl text-sm text-white bg-yellow-400 hover:bg-yellow-300 text-black font-bold disabled:opacity-60" data-testid="mc-connect">{mcSaving ? 'Connecting…' : 'Connect Mailchimp'}</button>
            </div>
          </div>
        )}
      </div>

      {/* Quick-start cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <QuickStart title="Slack" desc="Paste an Incoming Webhook URL. Choose the Slack format so posts render as chat messages." icon={MessageSquare} color="#4A154B" />
        <QuickStart title="Zapier / Make / n8n" desc="Create a 'Webhook' trigger, copy its URL here, pick events." icon={Zap} color="#FF4A00" />
        <QuickStart title="Discord" desc="Server → Integrations → Webhooks → New webhook. Paste URL here." icon={Webhook} color="#5865F2" />
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white font-syne">Your Webhooks</h2>
        <button
          onClick={() => startEdit('new')}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_20px_rgba(139,92,246,0.3)]"
          data-testid="add-webhook-btn"
        >
          <Plus size={14} /> Add Webhook
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2].map((i) => <div key={i} className="h-24 bg-white/5 rounded-2xl animate-pulse" />)}</div>
      ) : webhooks.length === 0 ? (
        <div className="p-10 rounded-2xl border border-white/10 bg-white/5 text-center" data-testid="webhooks-empty">
          <Webhook className="w-10 h-10 mx-auto mb-3 text-[#64748B]" />
          <p className="text-white font-semibold">No webhooks yet</p>
          <p className="text-[#9CA3AF] text-sm mt-1 max-w-md mx-auto">
            Add your first one to get instant notifications in Slack or trigger Zapier automations whenever something happens on the marketplace.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {webhooks.map((wh) => (
            <div key={wh.webhook_id} className="p-5 rounded-2xl border border-white/10 bg-white/5" data-testid={`webhook-${wh.webhook_id}`}>
              <div className="flex flex-col md:flex-row md:items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <h3 className="text-white font-semibold">{wh.label || 'Webhook'}</h3>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${wh.enabled ? 'bg-green-500/15 border-green-500/30 text-green-300' : 'bg-white/5 border-white/10 text-[#64748B]'}`}>
                      {wh.enabled ? 'Enabled' : 'Paused'}
                    </span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${wh.format === 'slack' ? 'bg-[#4A154B]/30 border-[#4A154B]/60 text-[#C4B5FD]' : 'bg-white/5 border-white/10 text-[#9CA3AF]'}`}>
                      {wh.format === 'slack' ? 'Slack format' : 'JSON'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-[#9CA3AF] mb-2 font-mono truncate">
                    <button
                      onClick={() => { navigator.clipboard.writeText(wh.url); toast.success('URL copied'); }}
                      className="flex items-center gap-1 hover:text-white"
                      title="Copy URL"
                    ><Copy size={10} /></button>
                    <span className="truncate">{wh.url}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {(wh.events || []).map((e) => (
                      <span key={e} className="text-[10px] px-2 py-0.5 rounded-full bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 text-[#C4B5FD] font-mono">{e}</span>
                    ))}
                  </div>
                  <div className="text-[10px] text-[#64748B] flex flex-wrap gap-4">
                    <span>✓ {wh.deliveries_ok || 0} delivered</span>
                    <span>✗ {wh.deliveries_err || 0} errors</span>
                    {wh.last_status && (
                      <span className="flex items-center gap-1">
                        {wh.last_status === 'ok' ? <Check size={10} className="text-green-400" /> : <AlertCircle size={10} className="text-red-400" />}
                        Last: {wh.last_status} {wh.last_status_code ? `(${wh.last_status_code})` : ''}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  <button onClick={() => sendTest(wh.webhook_id)} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-[#9CA3AF]" title="Send test" data-testid={`test-${wh.webhook_id}`}><Send size={14} /></button>
                  <button onClick={() => toggleEnabled(wh)} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-[#9CA3AF]" title={wh.enabled ? 'Pause' : 'Enable'} data-testid={`toggle-${wh.webhook_id}`}>
                    {wh.enabled ? <AlertCircle size={14} /> : <Check size={14} />}
                  </button>
                  <button onClick={() => startEdit(wh)} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-[#9CA3AF]" title="Edit" data-testid={`edit-${wh.webhook_id}`}><Edit3 size={14} /></button>
                  <button onClick={() => remove(wh.webhook_id)} className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400" title="Delete" data-testid={`delete-${wh.webhook_id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4" onClick={() => setEditing(null)}>
          <div className="bg-[#1A1A3E] border border-white/10 rounded-2xl p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-syne text-lg font-bold text-white mb-4">{editing === 'new' ? 'Add Webhook' : 'Edit Webhook'}</h2>
            <div className="space-y-4">
              <div>
                <label className="text-[#9CA3AF] text-xs mb-1 block">Label</label>
                <input
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="e.g., Team Slack / Zapier flow / Make scenario"
                  className="w-full bg-white/5 border border-white/10 rounded-xl p-2.5 text-white text-sm"
                  data-testid="webhook-label-input"
                />
              </div>
              <div>
                <label className="text-[#9CA3AF] text-xs mb-1 block">Webhook URL *</label>
                <input
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  placeholder="https://hooks.slack.com/services/… or https://hooks.zapier.com/…"
                  className="w-full bg-white/5 border border-white/10 rounded-xl p-2.5 text-white text-sm font-mono"
                  data-testid="webhook-url-input"
                />
              </div>
              <div>
                <label className="text-[#9CA3AF] text-xs mb-1 block">Payload Format</label>
                <div className="grid grid-cols-2 gap-2">
                  {[{ v: 'generic', t: 'Generic JSON', d: 'For Zapier / Make / n8n / Discord / custom endpoints' }, { v: 'slack', t: 'Slack format', d: 'Posts a friendly text summary to a Slack channel' }].map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => setForm({ ...form, format: o.v })}
                      className={`text-left p-3 rounded-xl border text-xs ${form.format === o.v ? 'border-[#8B5CF6]/60 bg-[#8B5CF6]/10' : 'border-white/10 bg-white/5 hover:border-white/20'}`}
                      data-testid={`format-${o.v}`}
                    >
                      <div className="text-white font-semibold text-sm mb-0.5">{o.t}</div>
                      <div className="text-[#9CA3AF]">{o.d}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[#9CA3AF] text-xs mb-2 block">Events to listen for *</label>
                <div className="space-y-3">
                  {EVENT_GROUPS.map((g) => (
                    <div key={g.label}>
                      <div className="text-[10px] uppercase tracking-wide text-[#64748B] mb-1">{g.label}</div>
                      <div className="flex flex-wrap gap-2">
                        {g.events.filter((e) => allowedEvents.includes(e)).map((e) => {
                          const on = form.events.includes(e);
                          return (
                            <button
                              key={e}
                              type="button"
                              onClick={() => setForm({ ...form, events: on ? form.events.filter((x) => x !== e) : [...form.events, e] })}
                              className={`text-xs px-3 py-1 rounded-full border font-mono ${on ? 'bg-[#8B5CF6]/20 border-[#8B5CF6]/60 text-white' : 'bg-white/5 border-white/10 text-[#9CA3AF] hover:border-white/20'}`}
                              data-testid={`event-${e}`}
                            >
                              {on ? '✓ ' : ''}{e}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6 pt-4 border-t border-white/10">
              <button
                onClick={save}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white text-sm font-semibold"
                data-testid="save-webhook-btn"
              >
                {editing === 'new' ? 'Create webhook' : 'Save changes'}
              </button>
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-xl text-sm text-[#9CA3AF] hover:text-white">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function QuickStart({ title, desc, icon: Icon, color }) {
  return (
    <div className="p-4 rounded-2xl border border-white/10 bg-white/5">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}25`, borderColor: `${color}40` }}>
          <Icon size={16} style={{ color }} />
        </div>
        <h3 className="text-white font-semibold text-sm">{title}</h3>
      </div>
      <p className="text-[#9CA3AF] text-xs">{desc}</p>
    </div>
  );
}
